import { useContext, useEffect, useRef, useState } from "react";
import ValidatedInput, { InputRef, isValid } from "../components/ValidatedInput";
import { Grid, Typography, Button, IconButton, Box } from "@material-ui/core";
import React from "react";
import Gridable from "../components/Gridable";
import { DeleteIcon } from "../components/Icons";
import { AppContext } from "../context/AppContext";
import { SEEmployeeRequest } from "../model/CfdiSe";
import { Employee } from "../model/Employee";
import translate from "../i18n/Translator";
import DatePicker from "../components/DatePicker";
import { EmployeeGroupResultImport } from "../model/EmployeeGroup";
import EmployeeGroupResultsImport from "../employeegroups/EmployeeGroupResultsImport";
import { isRoleOrParent } from "../model/Role";
import { useEmployeeSnackbar } from "./EmployeesSnackbar";

interface PrepareEmployeesProps {
    submitting: boolean;
    employees: Employee[];
    grantedToUpdate: boolean;
    seEmployees: SEEmployeeRequest[];
    from: string;
    to: string;
    canEdit: boolean;
    minDate?: Date;
    maxDate?: Date;
    repeatEmployees?: boolean;
    setEmployees(seEmployees: SEEmployeeRequest[]): any;
    promiseImportGroupEmployee(file: File): Promise<EmployeeGroupResultImport>;
    setSubmitting(submitting: boolean): any;
    mergeEmployeesValidations?(validations: any): any;
    validateEmployees?(seEmployees: SEEmployeeRequest[]): boolean;
}

export default function PrepareEmployees(props: PrepareEmployeesProps) {
    const context = useContext(AppContext);
    const { showMessage } = useEmployeeSnackbar();
    const [format] = useState("DD/MM/YYYY");
    const [employeeIds, setEmployeeIds] = useState<string[]>(props.employees.map((el) => el.id));
    const [employeeLabels, setEmployeeLabels] = useState<string[]>(props.employees.map((el) => el.name));
    const [employeeId, setEmployeeId] = useState<string>("");
    const [validations, setValidations] = useState({} as any);
    const fileInput = useRef<HTMLInputElement>(null);
    const [employeesNotRegistered, setEmployeesNotRegistered] = useState<Employee[]>([]);
    const [urlImportResults, seturlImportResults] = useState<string>("");
    const isContractProvider =
        isRoleOrParent(context.session!.role, "provider") && context.isGranted("ProviderContractsUpdate");

    const hasChangedEmployee = (name: string, value: string) => {
        setEmployeeId(!value || value === "---" ? "" : value);
    };

    const addAllEmployees = () => {
        let i = 0;
        const requestEmployees = props.employees.map((el) => {
            return {
                external_id: el.id + i++,
                employee_id: el.id,
                rfc: el.rfc,
                name: el.name,
                from: props.from,
                to: props.to,
                deletable: true,
            } as SEEmployeeRequest;
        });
        props.setEmployees(requestEmployees);
        if (!props.repeatEmployees) {
            setEmployeeIds([]);
            setEmployeeLabels([]);
            setEmployeeId("");
        }
    };

    const onChangedEmployeeDate =
        (employeeRequest: SEEmployeeRequest) => (name: string, raw?: string, date?: Date, inputRef?: InputRef) => {
            employeeRequest = { ...employeeRequest, [name]: raw };
            props.setEmployees(
                props.seEmployees.map((el) => {
                    if (el.external_id === employeeRequest.external_id) {
                        return employeeRequest;
                    }
                    return el;
                })
            );

            if (inputRef) {
                setValidations((prev: any) => ({
                    ...prev,
                    [`${name}_${employeeRequest.employee_id}`]: inputRef,
                }));
            }
        };

    const addFileEmployees = () => {
        fileInput.current!.click();
    };

    const getBackground = (employee: SEEmployeeRequest) => {
        return !!employee.inError;
    };

    const addEmployee = () => {
        if (!employeeId) return;

        const index = employeeIds.findIndex((el) => el === employeeId);
        if (index === -1) return;

        const employee = props.employees.find((el) => el.id === employeeId);
        if (!employee) return;

        const requestEmployees = props.seEmployees.map((el) => el);
        requestEmployees.push({
            external_id: employeeId + requestEmployees.length,
            employee_id: employeeId,
            rfc: employee.rfc,
            name: employee.name,
            from: props.from,
            to: props.to,
            deletable: true,
        });

        props.setEmployees(requestEmployees);
        if (!props.repeatEmployees) {
            setEmployeeIds(employeeIds.filter((el, i) => i !== index));
            setEmployeeLabels(employeeLabels.filter((el, i) => i !== index));
            setEmployeeId("");
        }
    };

    const deleteEmployee = (employeeRequest: SEEmployeeRequest, index: number) => () => {
        const employee = props.employees.find((el) => el.id === employeeRequest.employee_id);
        if (!employee) return;

        const currentEmployeeIds = props.seEmployees.map((el) => el.employee_id);
        const filteredEmployees = props.employees.filter(
            (el) => el.id === employee.id || currentEmployeeIds.indexOf(el.id) === -1
        );

        if (!props.repeatEmployees) {
            setEmployeeIds(filteredEmployees.map((el) => el.id));
            setEmployeeLabels(filteredEmployees.map((el) => el.name));
        }
        props.setEmployees(props.seEmployees.filter((el, i) => i !== index));

        setValidations((prevValidations: any) => {
            const newValidations = { ...prevValidations };
            delete newValidations[`from_${employeeRequest.employee_id}`];
            delete newValidations[`to_${employeeRequest.employee_id}`];
            return newValidations;
        });
    };
    
    const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }
        props.setSubmitting(true);

        props.promiseImportGroupEmployee(event.target.files[0]).then((response) => {
            if(response.employees === undefined || response.employees.length === 0){
                showMessage(translate("employeegroups.empty_import") as string, "error");
                return;
            }
            if(response.employees_not_registered.length > 0){
                setEmployeesNotRegistered(response.employees_not_registered);
                seturlImportResults(response.url_export_results);
                return;
            }
            let i = 0;
            const requestEmployees = response.employees
                .map(emp => {
                    return {
                        external_id: employeeId+i++,
                        employee_id: emp.employee_id,
                        rfc: emp.rfc,
                        name: emp.name,
                        from: emp.from || props.from,
                        to: emp.to || props.to,
                        deletable: true,
                    } as SEEmployeeRequest;
                });
            props.setEmployees(requestEmployees);
            if(!props.repeatEmployees){
                let tempEmployeees = props.employees.filter((e) =>  requestEmployees.filter(er => er.employee_id === e.id).length === 0);
                setEmployeeIds(tempEmployeees.map(e => e.id));
                setEmployeeLabels(tempEmployeees.map(e => e.name));
            }
            if(props.validateEmployees && !props.validateEmployees(requestEmployees)){
                return;
            }
            showMessage(translate("employeegroups.success") as string, "success");
        }).catch((error) => {
            showMessage(error.message, "error");
        }).finally(() => {
            props.setSubmitting(false);
            fileInput.current!.value = "";
        });
    }

    const { mergeEmployeesValidations } = props;

    useEffect(() => {
        if (mergeEmployeesValidations) {
            mergeEmployeesValidations(validations);
        }
    }, [validations, mergeEmployeesValidations]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant="subtitle2">{translate("cfdis_se.prepare.employees")}</Typography>
                <br />
                <Grid container justifyContent="center" alignContent="center" alignItems="center" spacing={2}>
                    <Grid item xs={12} sm>
                        <ValidatedInput
                            type="text"
                            id="employee_id"
                            name="employee_id"
                            value={employeeId}
                            label={translate("employees.single") as string}
                            options={employeeIds}
                            optionLabels={employeeLabels}
                            disabled={props.submitting || !props.canEdit}
                            margin="dense"
                            onValueChanged={hasChangedEmployee}
                        />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="medium"
                            disabled={!employeeId  || !props.canEdit}
                            onClick={addEmployee}
                        >
                            {translate("buttons.add")}
                        </Button>
                    </Grid>
                    {!isContractProvider && (
                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="medium"
                                disabled={props.employees.length === 0 || !props.canEdit}
                                onClick={addAllEmployees}
                            >
                                {translate("buttons.add_all")}
                            </Button>
                        </Grid>
                    )}
                    {!isContractProvider && context.isGranted("EmployeeGroupsCreate") && (
                        <Grid item xs={12} sm="auto">
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="medium"
                                disabled={props.employees.length === 0 || !props.canEdit}
                                onClick={addFileEmployees}
                            >
                                {translate("employeegroups.add_file")}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <Gridable
                    items={props.seEmployees}
                    loading={false}
                    background={getBackground}
                    empty={translate("cfdis_se.prepare.employees_empty") as string}
                    columns={[
                        {
                            title: translate("employees.rfc"),
                            converter: (item) => item.rfc || "---",
                            fullWidth: true,
                            xs: 2,
                            sm: 2,
                            md: 2,
                            lg: 2,
                            xl: 2,
                        },
                        {
                            title: translate("employees.name"),
                            converter: (item) => item.name || "---",
                            fullWidth: true,
                            xs: true,
                            sm: true,
                            md: true,
                            lg: true,
                            xl: true,
                        },
                        {
                            title: translate("contracts.from"),
                            converter: (item) => (
                                <DatePicker
                                    name="from"
                                    label={translate("contracts.from") as string}
                                    initial={item.from}
                                    format={format}
                                    clearable
                                    defaultIfMissing
                                    autoOK
                                    disableFuture={false}
                                    required
                                    disabled={props.submitting || !props.canEdit}
                                    minDate={props.minDate}
                                    maxDate={props.maxDate}
                                    onChange={onChangedEmployeeDate(item)}
                                />
                            ),
                            fullWidth: true,
                            xs: 2,
                            sm: 2,
                            md: 2,
                            lg: 2,
                            xl: 2,
                        },
                        {
                            title: translate("contracts.to"),
                            converter: (item) => (
                                <DatePicker
                                    name="to"
                                    label={translate("contracts.to") as string}
                                    initial={item.to}
                                    format={format}
                                    clearable
                                    defaultIfMissing
                                    autoOK
                                    disableFuture={false}
                                    required
                                    disabled={props.submitting || !props.canEdit}
                                    minDate={props.minDate}
                                    maxDate={props.maxDate}
                                    onChange={onChangedEmployeeDate(item)}
                                />
                            ),
                            fullWidth: true,
                            xs: 2,
                            sm: 2,
                            md: 2,
                            lg: 2,
                            xl: 2,
                        },
                        {
                            title: (
                                <IconButton size="small" style={{ visibility: "hidden" }} disabled>
                                    <DeleteIcon />
                                </IconButton>
                            ),
                            converter: (employee, index) => {
                                return employee.deletable ? (
                                    <IconButton
                                        aria-label="options"
                                        color="default"
                                        size="small"
                                        onClick={deleteEmployee(employee, index)}
                                        disabled={!props.canEdit}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                ) : (
                                    <Box p={2} />
                                );
                            },
                            fullWidth: true,
                            justify: "flex-end",
                            xs: "auto",
                        },
                    ]}
                    />
                    </Grid>
                {employeesNotRegistered.length > 0 && urlImportResults !== "" &&
                    <EmployeeGroupResultsImport
                        onClose={() => {setEmployeesNotRegistered([]); seturlImportResults("");}}
                        url={urlImportResults}
                        employees={employeesNotRegistered}
                    />
                }
                <input type="file" onChange={handleUpload} ref={fileInput} style={{ display: "none" }} accept=".xlsx" />
                </Grid>
    );
}
